import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { PublicNav } from "@/components/app/PublicNav";
import { VoiceInputButton } from "@/components/app/VoiceInputButton";
import { WorkflowSteps } from "@/components/app/WorkflowSteps";
import { DemoAnalytics } from "@/components/app/DemoAnalytics";
import { ArrowRight, Globe2, Search, FileText, GitCompareArrows, ShieldCheck, Boxes, Scale, CheckCircle2, Factory, Truck } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const HERO_IMG = "https://images.unsplash.com/photo-1590497008432-598f04441de8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBzaGlwcGluZyUyMHBvcnQlMjBjb250YWluZXIlMjBhZXJpYWx8ZW58MHx8fHwxNzkxMjcwOTk5fDA&ixlib=rb-4.1.0&q=85";
const LOG_IMG = "https://images.unsplash.com/photo-1494412519320-aa613dfb7738?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwyfHxnbG9iYWwlMjB0cmFkZSUyMGxvZ2lzdGljcyUyMGFic3RyYWN0fGVufDB8fHx8MTc5MTI3MDk5OXww&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  const [text, setText] = useState("");
  const [faqOpen, setFaqOpen] = useState(0);
  const { user } = useAuth();
  const nav = useNavigate();

  const submit = () => {
    if (!text.trim()) return;
    sessionStorage.setItem("gb_pending_request", text.trim());
    nav(user ? "/buyer/new" : "/signup?next=/buyer/new");
  };

  return (
    <div className="bg-[hsl(var(--navy))] text-slate-100 min-h-screen">
      <PublicNav />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-white/5">
        <img src={HERO_IMG} alt="Global shipping port" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-[hsl(var(--navy))]/70 via-[hsl(var(--navy))]/85 to-[hsl(var(--navy))]"></div>
        <div className="absolute inset-0 noise-overlay opacity-[0.04] mix-blend-overlay"></div>

        <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-24 md:pt-28 md:pb-32 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-7">
            <div className="eyebrow mb-5">Global B2B sourcing · Built for serious buyers</div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-6">
              Describe what you need.<br/>
              <span className="text-[hsl(var(--gold))]">Get vetted suppliers, side-by-side.</span>
            </h1>
            <p className="text-slate-300 text-base md:text-lg max-w-2xl leading-relaxed">
              GlobalBuy AI turns a plain-language sourcing need into a structured RFQ, matches you to international suppliers, collects quotations, and gives you a transparent AI comparison — so you can make a confident procurement decision.
            </p>

            {/* Composer */}
            <div className="mt-9 rounded-md border border-white/10 bg-[hsl(var(--navy-muted))]/80 backdrop-blur-xl p-3 md:p-4">
              <label htmlFor="hero-input" className="sr-only">Describe your sourcing need</label>
              <div className="flex items-start gap-2">
                <textarea
                  id="hero-input"
                  data-testid="hero-sourcing-input"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  rows={3}
                  placeholder={"e.g. 10,000 anodized aluminum enclosures (120×80×35 mm) delivered to Rotterdam within 8 weeks, budget €18,000 CIF."}
                  className="flex-1 bg-transparent outline-none text-slate-100 placeholder:text-slate-500 resize-none text-[15px] leading-relaxed px-3 py-2 max-h-48"
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); } }}
                />
                <VoiceInputButton onTranscript={setText} existingText={text} testId="hero-voice-btn" />
              </div>
              <div className="flex items-center justify-between pt-3 mt-2 border-t border-white/5">
                <div className="text-[11px] text-slate-500 tracking-wide">Enter to continue · Shift+Enter for a new line</div>
                <button onClick={submit} disabled={!text.trim()} className="btn-gold" data-testid="hero-submit-btn">
                  Analyze with AI <ArrowRight size={16}/>
                </button>
              </div>
            </div>

            <div className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-slate-400">
              <div className="inline-flex items-center gap-1.5"><ShieldCheck size={13} className="text-[hsl(var(--gold))]"/> Transparent supplier trust levels</div>
              <div className="inline-flex items-center gap-1.5"><Scale size={13} className="text-[hsl(var(--gold))]"/> Human-approved purchasing only</div>
              <div className="inline-flex items-center gap-1.5"><Globe2 size={13} className="text-[hsl(var(--gold))]"/> Multi-currency · International by design</div>
            </div>
          </div>

          <div className="hidden md:block md:col-span-5">
            <div className="relative aspect-[4/5] rounded-md overflow-hidden border border-white/10 shadow-2xl">
              <img src={LOG_IMG} alt="Global logistics" className="w-full h-full object-cover"/>
              <div className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--navy))] via-transparent to-transparent"/>
              <div className="absolute bottom-5 left-5 right-5">
                <div className="eyebrow mb-2">Live sourcing surface</div>
                <div className="font-display text-2xl leading-tight">12 countries · 460+ suppliers<br/>in the demo index</div>
                <div className="text-xs text-slate-400 mt-2">Demo supplier directory. Clearly labeled sample data.</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW */}
      <section id="how" className="max-w-7xl mx-auto px-6 py-20">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="eyebrow mb-3">How it works</div>
            <h2 className="font-display text-3xl md:text-4xl leading-tight">A full procurement workflow,<br/><span className="text-[hsl(var(--gold))]">in one surface.</span></h2>
            <p className="text-slate-400 mt-5 text-sm leading-relaxed">From an informal request to a structured RFQ, matched suppliers, scored quotations and an order record — without ever handing off the final decision to an autonomous system.</p>
          </div>
          <div className="md:col-span-8 grid sm:grid-cols-2 gap-4">
            {[
              { Icon: Search, title: "AI requirement extraction", body: "Natural-language in. Structured specifications out — reviewed and editable by you before anything moves forward." },
              { Icon: FileText, title: "Professional RFQs", body: "An editable, buyer-controlled RFQ is generated from your approved requirements, ready to send." },
              { Icon: Boxes, title: "Supplier discovery", body: "Match to a transparent international supplier index with clearly-labeled trust levels." },
              { Icon: GitCompareArrows, title: "Side-by-side analysis", body: "Price, lead time, risk, hidden costs — compared transparently, never a blind cheapest-wins pick." },
            ].map(({ Icon, title, body }) => (
              <div key={title} className="rounded-md border border-white/10 bg-[hsl(var(--navy-muted))] p-6">
                <Icon className="text-[hsl(var(--gold))]" size={22}/>
                <div className="font-display text-xl mt-3">{title}</div>
                <p className="text-slate-400 text-sm mt-2 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* USE CASES */}
      <WorkflowSteps />
      <DemoAnalytics />
      <section id="use-cases" className="border-t border-white/5 bg-[hsl(var(--navy-muted))]/40">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="eyebrow mb-3">Who it is for</div>
          <h2 className="font-display text-3xl md:text-4xl mb-10">Serious buyers. Serious suppliers.</h2>
          <div className="grid md:grid-cols-3 gap-5">
            {[
              { Icon: Factory, title: "Manufacturers", body: "Source components, raw materials, and specialized parts with controlled MOQ and lead-time tracking." },
              { Icon: Truck, title: "Importers & distributors", body: "Standardize RFQs across categories and vendor regions with multi-currency quotes and clear Incoterms." },
              { Icon: CheckCircle2, title: "Enterprise procurement", body: "Give procurement teams an auditable workflow with trust signals and transparent AI reasoning." },
            ].map(({ Icon, title, body }) => (
              <div key={title} className="rounded-md border border-white/10 bg-[hsl(var(--navy))] p-7">
                <Icon size={22} className="text-[hsl(var(--gold))]"/>
                <div className="font-display text-xl mt-4">{title}</div>
                <p className="text-slate-400 text-sm mt-2 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST */}
      <section id="trust" className="max-w-7xl mx-auto px-6 py-20 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="eyebrow mb-3">Trust &amp; transparency</div>
          <h2 className="font-display text-3xl md:text-4xl leading-tight">Verification states that mean something.</h2>
          <p className="text-slate-400 mt-4 text-sm leading-relaxed">
            We never falsely claim a supplier is verified. Every supplier card clearly shows one of four states so you always know what evidence we hold.
          </p>
        </div>
        <div className="md:col-span-7 grid sm:grid-cols-2 gap-4">
          {[
            ["Verified", "Business registration, documents and platform activity have been reviewed."],
            ["Verification Pending", "Supplier has submitted documents; review is in progress."],
            ["Information Provided", "Self-provided company information. Not independently verified."],
            ["Unverified", "No supporting information has been received yet."],
          ].map(([t, b]) => (
            <div key={t} className="rounded-md border border-white/10 bg-[hsl(var(--navy-muted))] p-5">
              <div className="text-[hsl(var(--gold))] text-sm font-medium">{t}</div>
              <div className="text-slate-400 text-sm mt-1 leading-relaxed">{b}</div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="border-t border-white/5 bg-[hsl(var(--navy-muted))]/40">
        <div className="max-w-4xl mx-auto px-6 py-20">
          <div className="eyebrow mb-3">FAQ</div>
          <h2 className="font-display text-3xl md:text-4xl mb-8">Common questions</h2>
          <div className="divide-y divide-white/10 border-y border-white/10">
            {[
              ["Does GlobalBuy AI place orders automatically?", "No. Any financial commitment requires explicit human approval. The platform never purchases, pays, or commits on your behalf."],
              ["Where does supplier data come from?", "The initial directory is a clearly-labeled demo supplier index. Real supplier discovery via vetted integrations is on the roadmap."],
              ["Which currencies and regions are supported?", "The platform is international by design — multi-currency, multi-country, with Incoterms-ready quotations."],
              ["How is the AI recommendation generated?", "By analyzing quotations on price, total cost, lead time, risk, certifications and warranty. We always explain the reasoning and never pick blindly."],
            ].map(([q, a], i) => (
              <button
                key={q}
                onClick={() => setFaqOpen(faqOpen === i ? -1 : i)}
                data-testid={`faq-item-${i}`}
                className="w-full text-left py-5 flex items-start justify-between gap-6 group"
              >
                <div>
                  <div className="font-display text-lg text-white group-hover:text-[hsl(var(--gold))] transition-colors">{q}</div>
                  {faqOpen === i && <div className="text-slate-400 text-sm mt-3 leading-relaxed">{a}</div>}
                </div>
                <div className="mt-1 text-[hsl(var(--gold))]">{faqOpen === i ? "–" : "+"}</div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <div className="eyebrow mb-2">Start sourcing</div>
            <h3 className="font-display text-3xl md:text-4xl">Ready to find your next supplier?</h3>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => nav("/signup")} className="btn-gold" data-testid="cta-signup-btn">Create free account <ArrowRight size={16}/></button>
            <button onClick={() => nav("/login")} className="btn-ghost-light" data-testid="cta-signin-btn">Sign in</button>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/5 py-10">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-slate-500">
          <div>© {new Date().getFullYear()} GlobalBuy AI — International B2B sourcing</div>
          <div>Human-approved procurement · Transparent AI · Privacy-first</div>
        </div>
      </footer>
    </div>
  );
}
